type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type PwaState = {
  registration: ServiceWorkerRegistration | null;
  installPrompt: InstallPrompt | null;
  standalone: boolean;
  updateAvailable: boolean;
  registrationFailed: boolean;
};

const listeners = new Set<() => void>();
let state: PwaState = {
  registration: null,
  installPrompt: null,
  standalone: false,
  updateAvailable: false,
  registrationFailed: false,
};
let started = false;

function publish(patch: Partial<PwaState>) {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

export const pwaStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot: () => state,
};

export function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && navigator.standalone === true)
  );
}

export function isIosDevice() {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function initializePwa() {
  if (started) return;
  started = true;
  const displayMode = window.matchMedia("(display-mode: standalone)");
  publish({ standalone: isStandalone() });
  displayMode.addEventListener("change", () => publish({ standalone: isStandalone() }));
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    publish({ installPrompt: event as InstallPrompt });
  });
  window.addEventListener("appinstalled", () => {
    publish({ installPrompt: null, standalone: true });
  });
  if (!("serviceWorker" in navigator) || !window.isSecureContext) return;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    publish({ updateAvailable: Boolean(state.registration?.waiting) });
  });
  void navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" }).then(
    (registration) => {
      const ready = registration.active
        ? Promise.resolve(registration)
        : navigator.serviceWorker.ready;
      void ready.then((activeRegistration) => {
        publish({
          registration: activeRegistration,
          updateAvailable: Boolean(activeRegistration.waiting),
        });
      });
      registration.addEventListener("updatefound", () => {
        const worker = registration.installing;
        worker?.addEventListener("statechange", () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller) {
            publish({ updateAvailable: true });
          }
        });
      });
      window.addEventListener("focus", () => {
        void registration.update().catch(() => {});
      });
    },
    () => publish({ registrationFailed: true }),
  );
}

export async function installPwa() {
  const prompt = state.installPrompt;
  if (!prompt) return;
  await prompt.prompt();
  await prompt.userChoice;
  publish({ installPrompt: null });
}

export function activatePwaUpdate() {
  const worker = state.registration?.waiting;
  if (!worker) return;
  // Only this tab reloads. Other tabs may still hold a draft or a recorded call.
  navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), {
    once: true,
  });
  worker.postMessage({ type: "ACTIVATE_UPDATE" });
}
